import { useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { AddPhotosButton } from '@/features/uploadMedia/AddPhotosButton/AddPhotosButton';
import { MaxFileQuantityForUpload } from '@/services/uploaderService';
import { ProgressBar } from '@/ui/progressBar';
import { useLocalization } from '@/services/localizationService';
import binIcon from '@/public/images/icons/bin.svg';
import { tutorialState } from '@/stores/tutorialStore';
import useImageSelectorToolbar from '@/features/imageEditor/imageSelectorToolbar/useImageSelectorToolbar';
import { useBulkUploadTutorial } from '@/features/tutorial/bulkUpload/useBulkUploadTutorial';
import { mediaShownInComparerState } from '@/stores/mediaStore';
import { useUser } from '@/services/userService';
import { ImageCard } from './ImageCard';

export function ImageSelectorToolbar({
  mediaMaxLength = MaxFileQuantityForUpload,
  isOpen = true,
}: {
  mediaMaxLength?: number;
  isOpen?: boolean;
}) {
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const [isBarOpen, setIsBarOpen] = useState(isOpen);

  const [progressValue, setProgressValue] = useState(0);
  const tutorial = useRecoilValue(tutorialState);

  // TODO: fix behavior
  // useScrollToLatestMedia(mediaContainerRef.current);

  const {
    completeMediaList,
    isSelectionModeEnabled,
    mediasToDownload,
    onChangeMediaShownInComparer,
    onBatchDeleteMedia,
    onDeleteMedia,
    onReprocessFailedMedia,
    onToggleMedia,
    toggleAllMedia,
    track,
  } = useImageSelectorToolbar(mediaMaxLength, setProgressValue);

  const { t, data } = useLocalization();
  const { bulkUploadTutorialId } = useBulkUploadTutorial();
  const mediaInComparer = useRecoilValue(mediaShownInComparerState);
  const { data: user } = useUser();

  return (
    <div
      className={`absolute top-0 ${
        isBarOpen ? 'right-0' : 'right-[-200px]'
      } transition-all ease-in-out w-[200px] h-[100%] bg-gray-450 flex flex-col items-center p-5 py-8 pt-6`}
    >
      <div
        className="arrow absolute top-[50%] left-[-16px] h-[64px] w-[16px] cursor-pointer bg-gray-450 flex justify-center items-center rounded-l-[16px] translate-y-[-50%]"
        onClick={() => {
          setIsBarOpen(!isBarOpen);
          track('image_selector', !isBarOpen ? 'opened' : 'closed');
        }}
      >
        {isBarOpen ? '>' : '<'}
      </div>
      <button
        type="button"
        onClick={() =>
          toggleAllMedia({
            shouldSelectAll:
              mediasToDownload.length !== completeMediaList.length,
          })
        }
        className={`overflow-hidden self-start btn btn--secondary uppercase transition-all cursor-pointer text-[9px] uppercase font-bold bg-overlay-white-20 border-0 px-[10px] py-[2px] ${
          isSelectionModeEnabled
            ? 'min-h-[32px] opacity-100 mb-4'
            : 'h-[0] opacity-0 mb-0'
        }`}
      >
        {mediasToDownload.length === completeMediaList.length
          ? t(data.common.deselectAll)
          : t(data.common.selectAll)}
      </button>
      <div className="overflow-y-auto" ref={mediaContainerRef}>
        {completeMediaList.map((m, i) => (
          <ImageCard
            id={i === 0 ? `${bulkUploadTutorialId}-step1` : ''}
            shouldLightUp={
              tutorial.id === bulkUploadTutorialId && tutorial.currentStep === 1
            }
            key={m.id}
            media={m}
            selected={mediasToDownload.map((media) => media.id).includes(m.id)}
            isCurrent={mediaInComparer.id === m.id}
            showDeleteButton={completeMediaList.length > 1}
            onChangeShownMedia={onChangeMediaShownInComparer}
            onReprocessFailedMedia={onReprocessFailedMedia}
            onDeleteMedia={onDeleteMedia}
            onToggleMedia={onToggleMedia}
          />
        ))}
      </div>
      {!isSelectionModeEnabled && !tutorial.isTutorialActive ? (
        <div
          className={`w-[100%] ${
            completeMediaList.length === MaxFileQuantityForUpload
              ? 'opacity-50 pointer-events-none'
              : 'opacity-100 pointer-events-auto'
          }`}
        >
          <div className="flex items-center justify-between p-3 pt-4">
            <p className="SB13 me-3">
              {completeMediaList.length}/{mediaMaxLength}
            </p>

            <ProgressBar
              progressValue={progressValue}
              className="bg-overlay-white-10 w-[100%]"
            />
          </div>
          <AddPhotosButton
            className={`btn btn--large btn--secondary ${
              user?.monetization?.hasIncompleteSubscription
                ? 'cursor-not-allowed bg-overlay-white-50 hover:bg-overlay-white-50'
                : 'cursor-pointer bg-overlay-white-20 hover:bg-overlay-white-15'
            } text-[16px] border-0`}
          />
        </div>
      ) : (
        <div
          onClick={() => onBatchDeleteMedia()}
          className={`w-[100%] flex justify-center items-center mt-4 ${
            mediasToDownload.length === completeMediaList.length
              ? 'opacity-50 pointer-events-none'
              : 'opacity-100 pointer-events-auto'
          }`}
        >
          <img
            {...binIcon}
            id={`${bulkUploadTutorialId}-step2`}
            alt="batch-delete"
            className={`w-[46px] h-[46px] cursor-pointer ${
              tutorial.currentStep === 2 && tutorial.id === bulkUploadTutorialId
                ? 'z-20 pointer-events-none'
                : 'z-10'
            }`}
          />
        </div>
      )}
    </div>
  );
}
